import React, { useState } from "react";
import Chart from "react-apexcharts";
import { FaDownload } from "react-icons/fa6";
import * as XLSX from "xlsx";
import MonthPicker from "../../../components/reusable-monthPicker/MonthPicker";
import Loader from "../../../components/reusable-loader/Loader";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch } from "react-redux";

const DownloadIcon = FaDownload as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface Props {
  data: { name: string; approved: number; rejected: number; pending: number }[];
  setDateRange: (range: { startDate: Date; endDate: Date }) => void;
  loading: boolean;
}

const ProctorRequestsChart: React.FC<Props> = ({
  data,
  setDateRange,
  loading,
}) => {
  const dispatch = useDispatch();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const handleMonthChange = (range: { startDate: Date; endDate: Date }) => {
    setSelectedMonth(range.startDate);
    setDateRange(range);
  };

  const handleDownload = () => {
    if (!data || data.length === 0) {
      dispatch(
        showSnackbar({
          message: "No data available to download.",
          type: "error",
        }),
      );
      return;
    }

    // Prepare sheet data
    const sheetData: any[] = [];
    sheetData.push(["Proctor Requests"]);
    sheetData.push(["S.No", "Library Name", "Approved", "Rejected", "Pending"]);

    const monthYear = selectedMonth.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });

    data.forEach((row, idx) => {
      sheetData.push([
        idx + 1,
        row.name,
        row.approved,
        row.rejected,
        row.pending,
      ]);
    });

    // Convert to worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Proctor Requests");

    // Export file
    XLSX.writeFile(workbook, `ProctorRequestsReport_${monthYear}.xlsx`);
  };

  const categories = data.map((d) => d.name);

  const maxValue = Math.max(
    50, // minimum axis limit
    ...data.map((d) => d.approved + d.rejected + d.pending), // largest total in your dataset
  );

  const options: ApexCharts.ApexOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: {
      bar: {
        horizontal: true,
        columnWidth: "40%",
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "13px",
              fontWeight: 900,
            },
          },
        },
      },
    },
    xaxis: {
      categories,
      min: 0,
      max: maxValue,
    },
    yaxis: {
      labels: {
        show: true,
        // trim: false, // 👈 don't trim labels
        style: {
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (val: number, opts?: any) => {
          return categories[opts.dataPointIndex];
        },
      },
    },
    states: {
      active: {
        filter: { type: "none" },
      },
      hover: {
        filter: { type: "none" },
      },
    },
    dataLabels: {
      enabled: false,
      formatter: (val: number) => {
        return val === 0 ? "" : val;
      },
    },
    colors: ["#2e8b57", "#8b0000", "#f4c994"],
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      markers: { shape: "circle" },
      onItemClick: { toggleDataSeries: false },
      onItemHover: { highlightDataSeries: false },
    },
    grid: { borderColor: "#e0e0e0" },
    noData: {
      text: "No data to display",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#666",
        fontSize: "16px",
      },
    },
  };

  const series =
    data?.length > 0
      ? [
          { name: "Approved", data: data.map((d) => d.approved) },
          { name: "Rejected", data: data.map((d) => d.rejected) },
          { name: "Pending", data: data.map((d) => d.pending) },
        ]
      : [];

  // utility check
  const hasValidData =
    data.length > 0 &&
    data.some((d) => d.approved > 0 || d.rejected > 0 || d.pending > 0);

  return (
    <div className="chart-box">
      <div className="chart-title">
        Proctor Requests
        <div className="reports-controls">
          <MonthPicker value={selectedMonth} onChange={handleMonthChange} />
          <button className="reports-download" onClick={handleDownload}>
            <DownloadIcon /> Download
          </button>
        </div>
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Loader />
        </div>
      ) : (
        <>
          {!hasValidData ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "#666",
                fontSize: "16px",
              }}
            >
              No data to display.
            </div>
          ) : (
            <Chart
              options={options}
              series={series?.length > 0 ? series : undefined}
              type="bar"
              height={400}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProctorRequestsChart;
